/**
 * DocumentClassifyDialog — Gestión Documental Fase B (Capa 2, 2026-10)
 * Clasifica un adjunto de email de un proveedor genérico: la IA propone
 * tipo/proveedor/importe/fecha, el admin revisa/edita y confirma el
 * archivado en Google Drive (Proveedores/{proveedor}/{año}).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, FileText, ExternalLink, FolderArchive } from 'lucide-react';
import { API_URL } from '../config';

const API = API_URL;

const TIPOS = [
  { value: 'factura', label: 'Factura' },
  { value: 'presupuesto', label: 'Presupuesto' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'nomina', label: 'Nómina' },
  { value: 'otro', label: 'Otro' },
];

export const DocumentClassifyDialog = ({ open, onOpenChange, token, emailId, attachment }) => {
  const [step, setStep] = useState('loading'); // loading | review | done
  const [form, setForm] = useState({ tipo_documento: 'otro', proveedor_nombre: '', importe: '', fecha_documento: '' });
  const [textPreview, setTextPreview] = useState('');
  const [remitente, setRemitente] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [resultDoc, setResultDoc] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open || !attachment) return;
    setStep('loading');
    setResultDoc(null);
    (async () => {
      try {
        const res = await axios.post(
          `${API}/documentos-archivados/classify-email-attachment`,
          { email_id: emailId, attachment_filename: attachment.filename },
          { headers }
        );
        const d = res.data;
        setForm({
          tipo_documento: d.tipo_documento || 'otro',
          proveedor_nombre: d.proveedor_nombre || '',
          importe: d.importe ?? '',
          fecha_documento: d.fecha_documento || '',
        });
        setTextPreview(d.text_preview || '');
        setRemitente(d.remitente || '');
        setStep('review');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'No se pudo analizar el documento');
        onOpenChange(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, attachment, emailId]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await axios.post(
        `${API}/documentos-archivados/confirm-classify`,
        {
          email_id: emailId,
          attachment_filename: attachment.filename,
          tipo_documento: form.tipo_documento,
          proveedor_nombre: form.proveedor_nombre || null,
          importe: form.importe === '' ? null : parseFloat(form.importe),
          fecha_documento: form.fecha_documento || null,
        },
        { headers }
      );
      setResultDoc(res.data.documento);
      setStep('done');
      toast.success('Documento archivado en Google Drive');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo archivar el documento');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-white max-h-[88vh] flex flex-col" data-testid="doc-classify-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FolderArchive className="h-5 w-5 text-cyan-400" />
            Clasificar documento
          </DialogTitle>
          <DialogDescription className="text-slate-400 truncate">{attachment?.filename}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-slate-400" data-testid="doc-classify-loading">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
              <p className="text-sm">Analizando documento con IA...</p>
            </div>
          )}

          {step === 'review' && (
            <>
              <p className="text-xs text-slate-500">Remitente: {remitente || 'desconocido'}</p>
              <div className="space-y-2">
                <Label className="text-slate-300">Tipo de documento</Label>
                <Select value={form.tipo_documento} onValueChange={(v) => setForm({ ...form, tipo_documento: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="doc-classify-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 text-white">
                    {TIPOS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Proveedor</Label>
                <Input
                  value={form.proveedor_nombre}
                  onChange={(e) => setForm({ ...form, proveedor_nombre: e.target.value })}
                  placeholder="Nombre de la empresa"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="doc-classify-proveedor-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-300">Importe (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.importe}
                    onChange={(e) => setForm({ ...form, importe: e.target.value })}
                    placeholder="0.00"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="doc-classify-importe-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Fecha</Label>
                  <Input
                    type="date"
                    value={form.fecha_documento}
                    onChange={(e) => setForm({ ...form, fecha_documento: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="doc-classify-fecha-input"
                  />
                </div>
              </div>
              {textPreview && (
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><FileText className="h-3 w-3" /> Texto extraído (vista previa)</p>
                  <p className="text-xs text-slate-400 whitespace-pre-wrap line-clamp-6">{textPreview}</p>
                </div>
              )}
            </>
          )}

          {step === 'done' && resultDoc && (
            <div className="space-y-3 py-4" data-testid="doc-classify-done">
              <div className="flex items-center gap-2 text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-sm">
                <FolderArchive className="h-4 w-4 shrink-0" />
                Archivado en: {resultDoc.drive_folder_path}
              </div>
              {resultDoc.drive_web_view_link && (
                <a
                  href={resultDoc.drive_web_view_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-cyan-400 text-sm hover:underline"
                  data-testid="doc-classify-drive-link"
                >
                  Ver en Google Drive <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          {step === 'review' && (
            <Button
              onClick={handleConfirm}
              disabled={confirming}
              className="bg-cyan-600 hover:bg-cyan-500"
              data-testid="doc-classify-confirm-btn"
            >
              {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderArchive className="h-4 w-4 mr-2" />}
              Confirmar y archivar en Drive
            </Button>
          )}
          {step === 'done' && (
            <Button onClick={() => onOpenChange(false)} variant="outline" className="border-slate-600 text-slate-200" data-testid="doc-classify-close-btn">
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentClassifyDialog;
